import React from 'react';
import styled, { keyframes, css } from 'styled-components';

export type SkeletonVariant = 'line' | 'rect' | 'circle';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

type StyledSkeletonProps = {
  $variant: SkeletonVariant;
  $width: string;
  $height: string;
  $animated: boolean;
};

const radiusFor = (variant: SkeletonVariant): string => {
  switch (variant) {
    case 'circle':
      return 'var(--radius-full)';
    case 'line':
      return 'var(--radius-pill)';
    default:
      return 'var(--radius-md)';
  }
};

const StyledSkeleton = styled.span<StyledSkeletonProps>`
  display: inline-block;
  width: ${(p) => p.$width};
  height: ${(p) => p.$height};
  border-radius: ${(p) => radiusFor(p.$variant)};
  background: linear-gradient(
    90deg,
    var(--color-surface-subtle) 0%,
    var(--color-surface-inset) 50%,
    var(--color-surface-subtle) 100%
  );
  background-size: 200% 100%;
  ${(p) =>
    p.$animated &&
    css`
      animation: ${shimmer} 1.4s ease-in-out infinite;
    `}
`;

export type SkeletonProps = {
  variant?: SkeletonVariant;
  width?: string | number;
  height?: string | number;
  animated?: boolean;
  className?: string;
};

const toCssLength = (value: string | number | undefined, fallback: string): string => {
  if (value === undefined) return fallback;
  return typeof value === 'number' ? `${value}px` : value;
};

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'line',
  width,
  height,
  animated = true,
  className,
}) => {
  const defaultHeight = variant === 'line' ? '12px' : variant === 'circle' ? '40px' : '80px';
  const defaultWidth = variant === 'circle' ? '40px' : '100%';
  return (
    <StyledSkeleton
      aria-hidden="true"
      className={className}
      $variant={variant}
      $width={toCssLength(width, defaultWidth)}
      $height={toCssLength(height, defaultHeight)}
      $animated={animated}
    />
  );
};
